/**
 * Greedy label placement: each label tries slots around its node and keeps
 * the first that stays on the board and hits no placed label and no node.
 * Required labels (start, target, current) may step out a row or two (with a
 * leader line) or fall back to the first in-bounds slot; optional ones are
 * dropped. By default the slots are below, above, right, left; with
 * `eightSlots` the four diagonals join them and each node's slots are tried
 * in order of how far they point away from its path neighbours.
 */
import type { RelaxNode, Size } from "./layout";

export const LABEL_MIN_FONT = 11;
export const LABEL_MAX_FONT = 18;
const CHAR_WIDTH = 0.6;
const LINE_HEIGHT = 1.1;
const GAP = 4;
const EDGE_MARGIN = 4;
const LABEL_PAD = 2;
const NODE_CLEARANCE = 2;

export type LabelSlot =
  | "below"
  | "above"
  | "right"
  | "left"
  | "below-right"
  | "below-left"
  | "above-right"
  | "above-left";

const DIAG = Math.SQRT1_2;
const SLOT_DIR: Record<LabelSlot, { x: number; y: number }> = {
  below: { x: 0, y: 1 },
  above: { x: 0, y: -1 },
  right: { x: 1, y: 0 },
  left: { x: -1, y: 0 },
  "below-right": { x: DIAG, y: DIAG },
  "below-left": { x: -DIAG, y: DIAG },
  "above-right": { x: DIAG, y: -DIAG },
  "above-left": { x: -DIAG, y: -DIAG },
};
const EIGHT: LabelSlot[] = [
  "below",
  "above",
  "right",
  "left",
  "below-right",
  "below-left",
  "above-right",
  "above-left",
];
/** Straight slots read best; they win a near-tie with a diagonal. */
const CARDINAL_BIAS = 0.1;

export interface PlaceOptions {
  eightSlots?: boolean;
  /** Per node, the direction away from its path neighbours (any length). */
  away?: Map<string, { x: number; y: number }>;
}

/** The slot order for one node under `eightSlots`. */
export function slotOrder(away?: {
  x: number;
  y: number;
}): [LabelSlot, number][] {
  const a = away ?? { x: 0, y: 0 };
  const len = Math.hypot(a.x, a.y);
  const order =
    len < 1e-6
      ? EIGHT.slice()
      : EIGHT.map((slot, i) => {
          const d = SLOT_DIR[slot];
          const score =
            (d.x * a.x + d.y * a.y) / len + (i < 4 ? CARDINAL_BIAS : 0);
          return { slot, score, i };
        })
          .sort((a, b) => b.score - a.score || a.i - b.i)
          .map((o) => o.slot);
  const vertical = order.filter((s) => s === "below" || s === "above");
  return [
    ...order.map((slot): [LabelSlot, number] => [slot, 0]),
    ...[1, 2].flatMap((row) =>
      vertical.map((slot): [LabelSlot, number] => [slot, row]),
    ),
  ];
}
/** Slot order; rows > 0 step a label further out when the near slots are taken. */
const SLOTS: [LabelSlot, number][] = [
  ["below", 0],
  ["above", 0],
  ["right", 0],
  ["left", 0],
  ["below", 1],
  ["above", 1],
  ["below", 2],
  ["above", 2],
];

export interface LabelRequest {
  id: string;
  text: string;
  fontSize: number;
  required: boolean;
  /** An optional label that may still step out a row (never overlaps). */
  stepOut?: boolean;
}

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PlacedLabel {
  id: string;
  /** Node edge to label box, when the label sits away from its node. */
  leader?: { x1: number; y1: number; x2: number; y2: number };
  text: string;
  fontSize: number;
  /** Centre x and baseline y, for textAnchor="middle". */
  x: number;
  y: number;
  slot: LabelSlot;
  box: Box;
}

/** Nominal size, shrunk to fit the board width, clamped to 11-18. */
export function labelFontSize(
  nominal: number,
  text: string,
  availableWidth: number,
): number {
  const fit = availableWidth / Math.max(1, text.length * CHAR_WIDTH);
  return Math.max(LABEL_MIN_FONT, Math.min(LABEL_MAX_FONT, nominal, fit));
}

const overlaps = (a: Box, b: Box) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

const hitsCircle = (box: Box, cx: number, cy: number, r: number) => {
  const nx = Math.max(box.x, Math.min(cx, box.x + box.w));
  const ny = Math.max(box.y, Math.min(cy, box.y + box.h));
  return Math.hypot(cx - nx, cy - ny) < r;
};

function slotBox(
  n: RelaxNode,
  slot: LabelSlot,
  row: number,
  w: number,
  h: number,
): Box {
  const step = row * (h + LABEL_PAD * 2);
  const d = n.r * DIAG + GAP;
  switch (slot) {
    case "below-right":
      return { x: n.px + d, y: n.py + d - h * 0.3, w, h };
    case "below-left":
      return { x: n.px - d - w, y: n.py + d - h * 0.3, w, h };
    case "above-right":
      return { x: n.px + d, y: n.py - d - h * 0.7, w, h };
    case "above-left":
      return { x: n.px - d - w, y: n.py - d - h * 0.7, w, h };
    case "below":
      return { x: n.px - w / 2, y: n.py + n.r + GAP + step, w, h };
    case "above":
      return { x: n.px - w / 2, y: n.py - n.r - GAP - h - step, w, h };
    case "right":
      return { x: n.px + n.r + GAP + 2, y: n.py - h / 2, w, h };
    default:
      return { x: n.px - n.r - GAP - 2 - w, y: n.py - h / 2, w, h };
  }
}

export function placeLabels(
  nodes: RelaxNode[],
  requests: LabelRequest[],
  size: Size,
  opts: PlaceOptions = {},
): PlacedLabel[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const placed: PlacedLabel[] = [];
  for (const req of requests) {
    const n = byId.get(req.id);
    if (!n) continue;
    const w = req.text.length * req.fontSize * CHAR_WIDTH;
    const h = req.fontSize * LINE_HEIGHT;
    // Only required labels may step out a row; an optional one stays beside
    // its node or is dropped.
    const all = opts.eightSlots ? slotOrder(opts.away?.get(req.id)) : SLOTS;
    const slots =
      req.required || req.stepOut ? all : all.filter(([, row]) => row === 0);
    const candidates = slots.map(([slot, row]) => {
      const box = slotBox(n, slot, row, w, h);
      if (slot === "below" || slot === "above") {
        box.x = Math.max(
          EDGE_MARGIN,
          Math.min(size.w - EDGE_MARGIN - w, box.x),
        );
      }
      return { slot, row, box };
    });
    const inBounds = (box: Box) =>
      box.x >= 0 &&
      box.y >= 0 &&
      box.x + box.w <= size.w &&
      box.y + box.h <= size.h;
    const hits = (box: Box) => {
      const padded = {
        x: box.x - LABEL_PAD,
        y: box.y - LABEL_PAD,
        w: box.w + 2 * LABEL_PAD,
        h: box.h + 2 * LABEL_PAD,
      };
      return (
        placed.filter((p) => overlaps(padded, p.box)).length +
        nodes.filter(
          (o) =>
            o.id !== n.id && hitsCircle(box, o.px, o.py, o.r + NODE_CLEARANCE),
        ).length
      );
    };
    const free = candidates.find(({ box }) => inBounds(box) && hits(box) === 0);
    let fallback: (typeof candidates)[number] | null = null;
    if (req.required && !free) {
      const ok = candidates.filter((c) => inBounds(c.box));
      // Eight-slot mode takes the least-crowded slot; the default the first.
      fallback = opts.eightSlots
        ? ok.reduce<(typeof ok)[number] | null>(
            (best, c) => (!best || hits(c.box) < hits(best.box) ? c : best),
            null,
          )
        : (ok[0] ?? null);
      fallback = fallback ?? candidates[0];
    }
    const pick = free ?? fallback;
    if (!pick) continue;
    const { box, slot } = pick;
    const far = opts.eightSlots && pick.row > 0;
    placed.push({
      id: req.id,
      ...(far ? { leader: leaderLine(n, box) } : {}),
      text: req.text,
      fontSize: req.fontSize,
      x: box.x + box.w / 2,
      y: box.y + req.fontSize * 0.85,
      slot,
      box,
    });
  }
  return placed;
}

/** From the node's rim toward the nearest point of the label box. */
function leaderLine(n: RelaxNode, box: Box) {
  const x2 = Math.max(box.x, Math.min(n.px, box.x + box.w));
  const y2 = Math.max(box.y, Math.min(n.py, box.y + box.h));
  const d = Math.hypot(x2 - n.px, y2 - n.py) || 1;
  const rim = n.r + 2;
  return {
    x1: n.px + ((x2 - n.px) / d) * rim,
    y1: n.py + ((y2 - n.py) / d) * rim,
    x2,
    y2,
  };
}
